import type { PageAction, PrimaryPageAction } from "@octopusdeploy/design-system-components";
import { Checkbox, List, ListDescription, ListItemContent, ListItemLink, ListTitle, Pagination, SearchInput } from "@octopusdeploy/design-system-components";
import { Avatar } from "@octopusdeploy/design-system-octopus-components";
import { useQuery } from "@octopusdeploy/octopus-react-client";
import type { ResourceCollection, UserResource, Repository, LicenseUsageResource, LicenseStatusResource } from "@octopusdeploy/octopus-server-client";
import { Permission } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import type { QueryParamValuesSetter } from "@octopusdeploy/portal-routes";
import { useCallback } from "react";
import { useAnalyticSimpleActionDispatch } from "~/analytics/Analytics";
import { useDialogTrigger } from "~/components/Dialog/DialogTrigger";
import { PageContent } from "~/components/PageContent/PageContent";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import { UpsellDialogWithLicenseUsage } from "~/components/UpsellDialog/UpsellDialogWithLicenseUsage";
import { EntitlementLimitPopUpEvent } from "~/components/UpsellDialog/upsellDialogAnalytics";
import { useDebouncedExternalState } from "~/hooks/useDebouncedExternalState";
import { getLicenseLimit } from "~/hooks/useLicenseLimit";
import { InviteOctoIdUserDialogLink } from "./InviteOctoIdUserDialogLink";
import { isOctopusIdEnabled } from "./identityProviders";
import styles from "./style.module.less";
const defaultPageSize = 30;
const pageSizeOptions = [defaultPageSize, 50, 100];
export const usersPageTitle = "Users";
interface UsersPageQueryParams {
    isServiceAccount: boolean;
    filter: string;
    page: number | undefined;
    pageSize: number | undefined;
}
export interface UsersPageLoaderData {
    usersResponse: ResourceCollection<UserResource>;
    isOctoIdEnabled: boolean;
    licenseUsage: LicenseUsageResource | null;
    licenseStatus: LicenseStatusResource | null;
}
export const usersPageLoader = async (repository: Repository, queryParams: UsersPageQueryParams): Promise<UsersPageLoaderData> => {
    const licenseUsageAsync = repository.Licenses.getCurrentUsage();
    const licenseStatusAsync = repository.Licenses.getCurrentStatus();
    const usersResponseAsync = getUsers(repository, queryParams.isServiceAccount, queryParams.filter, queryParams.page, queryParams.pageSize);
    const isOctoIdEnabledAsync = isOctopusIdEnabled();
    return {
        usersResponse: await usersResponseAsync,
        isOctoIdEnabled: await isOctoIdEnabledAsync,
        licenseUsage: await licenseUsageAsync,
        licenseStatus: await licenseStatusAsync,
    };
};
interface UsersPageComponentProps {
    loaderData: UsersPageLoaderData;
    queryParams: UsersPageQueryParams;
    setQueryParams: QueryParamValuesSetter<UsersPageQueryParams>;
}
export function UsersPage({ loaderData, queryParams, setQueryParams }: UsersPageComponentProps) {
    const { isLimitReached: isUserLimitReached, effectiveLicensedLimit } = getLicenseLimit(loaderData.licenseUsage, "users");
    const { isOpen: isUserLimitDialogOpen, openDialog: openUserLimitDialog, closeAllDialogs: closeUserLimitDialog } = useDialogTrigger();
    const simpleDispatchAction = useAnalyticSimpleActionDispatch();
    const [debouncedFilter, setDebouncedFilter] = useDebouncedExternalState(queryParams.filter, (value) => setQueryParams({ ...queryParams, filter: value, page: 1 }), 200);
    const [viewServiceAccounts, setViewServiceAccounts] = useDebouncedExternalState(queryParams.isServiceAccount, (value) => setQueryParams({ ...queryParams, isServiceAccount: value, page: 1 }), 200);
    const { result: users } = useQuery((repository) => getUsers(repository, queryParams.isServiceAccount, queryParams.filter, queryParams.page, queryParams.pageSize), [queryParams.isServiceAccount, queryParams.filter, queryParams.page, queryParams.pageSize], "Load users", {
        initialResult: loaderData.usersResponse,
    });
    const onPageChange = useCallback((page: number) => {
        setQueryParams({ ...queryParams, page });
    }, [queryParams, setQueryParams]);
    const onPageSizeChange = useCallback((pageSize: number) => {
        setQueryParams({ ...queryParams, pageSize, page: 1 });
    }, [queryParams, setQueryParams]);
    const getPageActions = (): [
        PrimaryPageAction | undefined,
        PageAction[]
    ] => {
        const isOctoIdEnabled = loaderData.isOctoIdEnabled;
        if (isOctoIdEnabled === undefined) {
            return [undefined, []];
        }
        const upsellDialogPageAction = (label: string): PrimaryPageAction => ({
            type: "button",
            hasPermissions: isAllowed({ permission: Permission.UserEdit }),
            label,
            onClick: () => openUserLimitDialogAndSendAnalytics?.(),
        });
        const createServiceAccountUpsellAction: PageAction = {
            type: "button",
            hasPermissions: isAllowed({ permission: Permission.UserEdit }),
            label: "Create Service Account",
            onClick: () => openUserLimitDialogAndSendAnalytics?.(),
            buttonType: "secondary",
        };
        const upsellActions: [
            PrimaryPageAction,
            PageAction[]
        ] = isOctoIdEnabled ? [upsellDialogPageAction("Add Users"), [createServiceAccountUpsellAction]] : [upsellDialogPageAction("Invite User"), []];
        const addUserPrimaryPageAction: PrimaryPageAction = {
            type: "navigate",
            hasPermissions: isAllowed({ permission: Permission.UserEdit }),
            label: "Add Users",
            path: links.createUserPage.generateUrl(),
        };
        const createServiceAccountPageAction: PageAction = {
            type: "navigate",
            buttonType: "secondary",
            hasPermissions: isAllowed({ permission: Permission.UserEdit }),
            label: "Create Service Account",
            path: links.createUserPage.generateUrl(),
        };
        const inviteUserPrimaryPageAction: PrimaryPageAction = {
            type: "custom",
            hasPermissions: isAllowed({ permission: Permission.UserEdit }),
            key: "Invite User",
            content: <InviteOctoIdUserDialogLink label="Invite User" serialNumber={loaderData.licenseStatus?.SerialNumber ?? ""}/>,
        };
        if (isUserLimitReached)
            return upsellActions;
        return isOctoIdEnabled ? [inviteUserPrimaryPageAction, [createServiceAccountPageAction]] : [addUserPrimaryPageAction, []];
    };
    function openUserLimitDialogAndSendAnalytics() {
        openUserLimitDialog();
        simpleDispatchAction(EntitlementLimitPopUpEvent);
    }
    const [primaryPageAction, pageActions] = getPageActions();
    return (<>
            <PageContent header={{ title: usersPageTitle, primaryAction: primaryPageAction, pageActions }} filters={{
            inputs: [
                <SearchInput value={debouncedFilter} onChange={setDebouncedFilter} accessibleName="Filter by user" placeholder="Filter by user"/>,
                <Checkbox label="View service accounts only" value={viewServiceAccounts} onChange={(selectedOnly) => setViewServiceAccounts(selectedOnly)}/>,
            ],
        }} pagination={{
            ui: (<Pagination label={usersPageTitle} totalResults={users.TotalResults} itemsPerPageOptions={pageSizeOptions} selectedItemsPerPage={users.ItemsPerPage} currentPage={queryParams.page ?? 1} onPageChange={onPageChange} onPageSizeChange={onPageSizeChange}/>),
            placement: "topAndBottom",
        }}>
                <List items={users.Items} renderRow={(props) => <UserRow user={props.item}/>}/>
            </PageContent>
            <UpsellDialogWithLicenseUsage title="Ready to grow your team?" content={<>
                        You've reached your <strong>{effectiveLicensedLimit}-user</strong> limit. Upgrade to add unlimited users, or remove users who no longer need access.
                    </>} open={isUserLimitDialogOpen} onClose={closeUserLimitDialog} licenseSerialNumber={loaderData.licenseStatus?.SerialNumber} licenseUsage={loaderData.licenseUsage}/>
        </>);
}
function UserRow({ user }: {
    user: UserResource;
}) {
    const usernameForAvatarText = user && user.DisplayName ? user.DisplayName : "";
    return (<ListItemLink href={links.userPage.generateUrl({ userId: user.Id })}>
            <ListItemContent>
                <div className={styles.row}>
                    <div className={styles.avatar}>
                        <Avatar type="user" size="medium" isServiceUser={user && user.IsService} src={user.Links?.Avatar} alt={usernameForAvatarText} text={usernameForAvatarText}/>
                    </div>
                    <div>
                        <ListTitle>{user.DisplayName}</ListTitle>
                        <ListDescription description={user.EmailAddress || user.Username}/>
                    </div>
                </div>
            </ListItemContent>
        </ListItemLink>);
}
async function getUsers(repository: Repository, isServiceAccount = false, filter = "", page = 1, pageSize = defaultPageSize): Promise<ResourceCollection<UserResource>> {
    return repository.Users.list({
        isServiceAccount: isServiceAccount,
        filter: filter,
        skip: (page - 1) * pageSize,
        take: pageSize,
    });
}
